import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { LOOKUP_CIRCLE, LOOKUP_ICON, getPathLookups } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';
import { device } from 'utils/device';

/**
 * ParticleCore — the SoA (structure-of-arrays) particle store of the
 * 'particles-optimised' home WebGL type.
 *
 * One contiguous index space, capacity = HomeFieldConfig.total() (the same
 * partition table the object-based particles type slices its HomeParticle
 * array with — shared truth, imported read-only). Each pool (intro / icon /
 * screens / spaces / systems / transitions) owns a contiguous [start, end)
 * range, so per-pool kernels iterate contiguous memory.
 *
 * Stage-1 lane set (the scaffold renders static points): position, scale,
 * alpha, tint, texture frame and state flags. The lane set GROWS per
 * implementation stage — integrator lanes (tx/ty/ease), parallax, repell,
 * path, physics and depth lanes arrive with their kernels (stages 2-6 of the
 * approved design). Kernels read/write lanes directly; there is deliberately
 * no per-particle object and no per-particle method dispatch — that dispatch
 * overhead is the ~17% of frame time this type exists to remove.
 */

export type PoolName = 'transitions' | 'intro' | 'icon' | 'screens' | 'spaces' | 'systems';

/** Contiguous pool slice. `end` is EXCLUSIVE (HomeFieldConfig.indexes() upper
 *  bounds are inclusive — converted once here so every loop is `i < end`). */
export interface PoolRange {
	name: PoolName;
	start: number;
	end: number;
	count: number;
}

//stateFlags bits:
export const FLAG_ACTIVE = 1 << 0;

//behaviourMask bits (one per kernel; kernels early-continue on their bit):
export const BEHAVIOUR_PROGRESS_PARALLAX = 1 << 0;
//ParticleProgressParallax.disableTimelineX variant — targetX excludes
//containerX/drag and xOffset is raw px, not a container-width fraction:
export const BEHAVIOUR_DISABLE_TIMELINE_X = 1 << 1;
export const BEHAVIOUR_MOUSE_PARALLAX = 1 << 2;
export const BEHAVIOUR_REPELL = 1 << 3;
//The two ParticlePath instances every HomeParticle carries (stage 4) — the
//orbit slot (HomeParticle.orbitPath, CIRCLE by default) and the icon slot
//(HomeParticle.iconPath, ICON by default). One bit per slot, matching the
//behaviour-manager add-order: orbitPath is added before iconPath:
export const BEHAVIOUR_PATH_ORBIT = 1 << 4;
export const BEHAVIOUR_PATH_ICON = 1 << 5;
//ParticleProperties' breathing scale oscillation (stage 4) — reference
//_processBreathing runs inside animatedProps.tick's _setScale, i.e. AFTER
//the scale Inertia tick; the breathe kernel therefore runs after integrate:
export const BEHAVIOUR_BREATHING = 1 << 6;
//ParticlePhysics (stage 5) — the screens drop machine's velocity/gravity
//integrator with the ParticleHitTests readback. Behaviour add-order slot:
//after depth, before repell:
export const BEHAVIOUR_PHYSICS = 1 << 7;
//ParticleDepth (stage 5) — bounce depth -> perspective shift + scale/alpha
//ranges. Add-order slot: after the paths/counters, before physics:
export const BEHAVIOUR_DEPTH = 1 << 8;

//Per-slot path flag bits (pathFlags lanes — ParticlePath's per-instance
//booleans, which are config not per-frame state):
/** _scaleActive: drive targetScale from the table's scale channel. */
export const PATH_FLAG_SCALE_ACTIVE = 1 << 0;
/** syncRotation: write sprite rotation from the path index (icon tri shapes). */
export const PATH_FLAG_SYNC_ROTATION = 1 << 1;
/** scaleAffect (reference default TRUE): multiply the path offset by
 *  sceneProps.zones.spaces.scale — yes, the SPACES zone scale, even on the
 *  icon/home orbit paths (reference quirk, kept bug-compatible). Intro
 *  clears it (startState sets scaleAffect = false). */
export const PATH_FLAG_SCALE_AFFECT = 1 << 2;

//ParticlePath's two Inertias are new Inertia(5, 1) — fixed ease 5, and value
//AND target start at 1 (Inertia's ctor promotes a truthy initial value into
//the target):
export const PATH_EASE = 5;

//repellType lane values (full RepellType set as of stage 5 — POSITION is the
//swirl's rel-space nudge + returnTo lerp, VECTOR feeds the physics vectors):
export const REPELL_NONE = 0;
export const REPELL_PUSH = 1;
export const REPELL_BOUNDARY = 2;
export const REPELL_POSITION = 3;
export const REPELL_VECTOR = 4;

//repellFlags bits:
/** ParticleRepell.returnTo is set (POSITION type's home-pull lerp). */
export const REPELL_HAS_RETURN = 1 << 0;

//vecFlags bits — Inertia's vector-movement mode per axis
//(ParticleProperties.setVectorStyleMovement / _x/_y.setVectorConfig):
export const VEC_X = 1 << 0;
export const VEC_Y = 1 << 1;

//physFlags bits (ParticlePhysics private state):
/** _allowYBounce (reference default true). */
export const PHYS_Y_BOUNCE = 1 << 0;
/** _rain — bottom-of-boundary respawn instead of bounce. */
export const PHYS_RAIN = 1 << 1;
/** _processBoundaryTick (reference default true; leftDrop('screens') clears it). */
export const PHYS_BOUNDARY = 1 << 2;
/** isLerping — the lerp machine owns the whole tick while set. */
export const PHYS_LERPING = 1 << 3;

//depthFlags bits (ParticleDepth private state):
/** _bounce (reference default true). */
export const DEPTH_BOUNCE = 1 << 0;

const POOL_NAMES: ReadonlyArray<PoolName> = ['transitions', 'intro', 'icon', 'screens', 'spaces', 'systems'];

/**
 * One ParticlePath instance as lanes (stage 4). Two of these live on the
 * core — orbit and icon — because every reference HomeParticle carries two
 * ParticlePath behaviours. Lane <-> reference member mapping:
 *   idx            _pathIndex        (randomise(): rand * (table.length - 1))
 *   speed          _speed            (default 1)
 *   dir            _direction        (default 1; randomise(direction) flips)
 *   xMul / yMul    _xMuliplier/_yMuliplier (default 1)
 *   expand(+target)    _expandMuliplier Inertia(5, 1) — value and target
 *   speedMul(+target)  _speedMulipler  Inertia(5, 1)
 *   scaleIdx       _scaleIndex       (only advanced when SCALE_ACTIVE)
 *   scaleMin/Max   _scaleRange
 *   lookupId       _path             (index into pathLookups' flattened tables)
 *   flags          PATH_FLAG_* bits  (scaleActive / syncRotation / scaleAffect)
 */
export class PathSlotLanes {
	public readonly idx: Float32Array;
	public readonly speed: Float32Array;
	public readonly dir: Float32Array;
	public readonly xMul: Float32Array;
	public readonly yMul: Float32Array;
	public readonly expand: Float32Array;
	public readonly expandTarget: Float32Array;
	public readonly speedMul: Float32Array;
	public readonly speedMulTarget: Float32Array;
	public readonly scaleIdx: Float32Array;
	public readonly scaleMin: Float32Array;
	public readonly scaleMax: Float32Array;
	public readonly lookupId: Uint8Array;
	public readonly flags: Uint8Array;

	constructor(capacity: number, defaultLookupId: number) {
		this.idx = new Float32Array(capacity);
		this.speed = new Float32Array(capacity);
		this.dir = new Float32Array(capacity);
		this.xMul = new Float32Array(capacity);
		this.yMul = new Float32Array(capacity);
		this.expand = new Float32Array(capacity);
		this.expandTarget = new Float32Array(capacity);
		this.speedMul = new Float32Array(capacity);
		this.speedMulTarget = new Float32Array(capacity);
		this.scaleIdx = new Float32Array(capacity);
		this.scaleMin = new Float32Array(capacity);
		this.scaleMax = new Float32Array(capacity);
		this.lookupId = new Uint8Array(capacity);
		this.flags = new Uint8Array(capacity);

		//Reference defaults (ParticlePath ctor/fields): speed 1, direction 1,
		//multipliers 1, both Inertias value=target=1, scaleAffect true. The
		//per-particle path-index randomise() every HomeParticle gets at
		//creation happens in ParticleCore's constructor (it needs the
		//flattened table lengths):
		this.speed.fill(1);
		this.dir.fill(1);
		this.xMul.fill(1);
		this.yMul.fill(1);
		this.expand.fill(1);
		this.expandTarget.fill(1);
		this.speedMul.fill(1);
		this.speedMulTarget.fill(1);
		this.lookupId.fill(defaultLookupId);
		this.flags.fill(PATH_FLAG_SCALE_AFFECT);
	}
}

export class ParticleCore {
	public readonly capacity: number;

	//Lanes (indexed by particle id). Public on purpose: kernels and the
	//renderer iterate them directly — accessor indirection would reintroduce
	//exactly the dispatch cost this design removes.
	public readonly x: Float32Array;
	public readonly y: Float32Array;
	public readonly scale: Float32Array;
	public readonly alpha: Float32Array;
	public readonly tint: Uint32Array; //0xRRGGBB (the renderer owns any GPU byte-order swap)
	public readonly textureId: Uint8Array; //index into the renderer's frame table
	public readonly stateFlags: Uint32Array; //FLAG_* bits
	public readonly behaviourMask: Uint32Array; //BEHAVIOUR_* bits

	//Integrator lanes (stage 2) — replace ParticleProperties' four Inertia
	//instances. Reference math (utils/motion/Inertia.ts, ease path):
	//  value += ((target - value) / ease) * window.deltaTime
	//Reference ease defaults (ParticleProperties ctor): x/y 8, scale 5, alpha 3.
	public readonly tx: Float32Array;
	public readonly ty: Float32Array;
	public readonly easeX: Float32Array;
	public readonly easeY: Float32Array;
	public readonly tScale: Float32Array;
	public readonly easeScale: Float32Array;
	public readonly tAlpha: Float32Array;
	public readonly easeAlpha: Float32Array;

	//ProgressParallax lanes (stage 2) — ParticleProgressParallax inputs:
	public readonly relX: Float32Array; //timeline-relative position (0..1 of virtual width)
	public readonly relY: Float32Array; //0..1 of window height
	public readonly xOffset: Float32Array; //fraction of container width (raw px in DISABLE_TIMELINE_X mode)
	public readonly parallaxPower: Float32Array;
	//Pure parallax targets (stage 3): ParticleProgressParallax's absX/absY —
	//the target BEFORE mouse-parallax adds to it. Repell PUSH rebuilds its
	//targets from these (reference: ref.position.absX/absY), which silently
	//discards the mouse-parallax contribution inside the radius — reference
	//behaviour, kept bug-compatible:
	public readonly pTx: Float32Array;
	public readonly pTy: Float32Array;

	//MouseParallax lanes (stage 3) — ParticleMouseParallax's two Inertias:
	public readonly mpX: Float32Array;
	public readonly mpY: Float32Array;
	public readonly mpEaseX: Float32Array;
	public readonly mpEaseY: Float32Array;
	public readonly mpPowX: Float32Array;
	public readonly mpPowY: Float32Array;

	//Repell lanes (stage 3) — ParticleRepell PUSH/BOUNDARY:
	public readonly repellType: Uint8Array; //REPELL_* values
	public readonly repellRadius: Float32Array;
	public readonly repellPower: Float32Array; //BOUNDARY ring distance (PUSH ignores it — see kernel)
	public readonly tScaleSaved: Float32Array; //targetScaleSaved: restored when outside the radius

	//Path slots (stage 4) — HomeParticle's two ParticlePath behaviours:
	public readonly orbitPath: PathSlotLanes; //HomeParticle.orbitPath (CIRCLE default)
	public readonly iconPath: PathSlotLanes; //HomeParticle.iconPath (ICON default)

	//Sprite rotation (stage 4). ONLY the path kernel's syncRotation branch
	//writes it — the reference never eases rotation (ParticleProperties.tick
	//ticks x/y/alpha/scale but not _rotation, and never writes
	//sprite.rotation; targetRotation writes land in a dead Inertia). Kept
	//bug-compatible: reposition passes do NOT reset this lane:
	public readonly rotation: Float32Array;

	//Breathing lanes (stage 4) — ParticleProperties._breathing (the icon
	//department's startBreathing scale oscillation). Reference math
	//(_processBreathing): add += dir * changePerTick * deltaTime, direction
	//flips at ±max, then targetScale = targetScaleSaved = base + add:
	public readonly breathBase: Float32Array;
	public readonly breathChange: Float32Array; //changePerTick
	public readonly breathMax: Float32Array; //maxChange
	public readonly breathAdd: Float32Array; //addValue (running)
	public readonly breathDir: Float32Array; //±1

	//Vector-movement lanes (stage 5) — Inertia's _processVector mode for the
	//x/y Inertias (ParticleProperties.setVectorStyleMovement; the halftone's
	//"whoozy" drift). vecFlags VEC_X/VEC_Y switch integrate's per-axis path;
	//the rate/max/dampen/reduceAt values are written by writeVectorConfig
	//(the setVectorConfig port — device-scaled + per-call randomised):
	public readonly vecFlags: Uint8Array;
	public readonly vecIncX: Float32Array; //_vectorIncreaseRate
	public readonly vecIncY: Float32Array;
	public readonly vecMaxX: Float32Array; //_maxVectorForce
	public readonly vecMaxY: Float32Array;
	public readonly vecDampX: Float32Array; //_dampen
	public readonly vecDampY: Float32Array;
	public readonly vecReduceAtX: Float32Array; //_reduceForceAt
	public readonly vecReduceAtY: Float32Array;
	public readonly vecForceX: Float32Array; //_vectorForce (running)
	public readonly vecForceY: Float32Array;

	//Repell POSITION extras (stage 5) — returnTo / returnPower:
	public readonly repellFlags: Uint8Array;
	public readonly repellReturnX: Float32Array;
	public readonly repellReturnY: Float32Array;
	public readonly repellReturnPowX: Float32Array; //returnPower (default 0.08)
	public readonly repellReturnPowY: Float32Array;

	//Physics lanes (stage 5) — ParticlePhysics state. Velocities/positions are
	//in the reference's REL units (fractions of container width/height):
	public readonly vx: Float32Array; //_vectorX
	public readonly vy: Float32Array; //_vectorY
	public readonly gravity: Float32Array; //(reference default 0.0002)
	public readonly physFlags: Uint8Array; //PHYS_* bits
	public readonly hitCooldown: Float32Array; //_processHHitCounter
	//The lerp machine (lerpToPosition / _processLerp):
	public readonly lerpStartX: Float32Array;
	public readonly lerpStartY: Float32Array;
	public readonly lerpEndX: Float32Array;
	public readonly lerpEndY: Float32Array;
	public readonly lerpEase: Uint8Array; //0 linear / 1..4 the lerp() ease ids
	public readonly lerpFrames: Float32Array; //currentLerpFrames
	public readonly lerpTargetFrames: Float32Array;
	//Target following (_processTarget): slot 0 = none, otherwise slot-1
	//indexes the frame-resolved targetsPx table the kernel receives:
	public readonly physTargetSlot: Uint8Array;
	public readonly tgtIncX: Float32Array; //_targetXIncrement
	public readonly tgtIncY: Float32Array;
	public readonly tgtDirX: Float32Array; //_xTargetDirection (±1)
	public readonly tgtDirY: Float32Array;

	//Depth lanes (stage 5) — ParticleDepth state (Inertia(5, 1) + config):
	public readonly depthVal: Float32Array; //_depthValue.value (init 1)
	public readonly depthTarget: Float32Array; //_depthValue.target (init 1)
	public readonly depthRatio: Float32Array; //_depthConfig.ratio
	public readonly depthBounceDir: Float32Array; //ctor: random ±1
	public readonly depthBounceSpeed: Float32Array; //ctor: 0.01 + rand * 0.09
	public readonly depthFlags: Uint8Array; //DEPTH_* bits (default BOUNCE)
	public readonly depthScaleMin: Float32Array; //_scale {min 0.1, max 0.43}
	public readonly depthScaleMax: Float32Array;
	public readonly depthAlphaMin: Float32Array; //_alpha {min 0.1, max 0.7}
	public readonly depthAlphaMax: Float32Array;
	public readonly origX: Float32Array; //_orginalCords (rel)
	public readonly origY: Float32Array;

	//Render sub-batch flag (stage 5): per-particle sprite.blendMode = 'add'
	//(the halftone) vs default. Consumed by the screens AND spaces (stage 6)
	//pools' SplitBlendParticleRenderer — other pools are container-level:
	public readonly blendAdd: Uint8Array;

	//Follow-swarm lanes (stage 6) — ParticleProgressParallax's followParticle
	//branch (the spaces organic-lines swarm). followIdx is the ABSOLUTE lane
	//id of the followed particle (-1 = none); the angle is stored in DEGREES
	//like the reference (_folllowOffsetAngleDegrees — rolled once per
	//assignment, never advanced; its >360 wrap check is a no-op kept in the
	//kernel). The kernel reads the followed lane's rel lanes as of the
	//PREVIOUS frame's physics write — one frame of lag vs the reference's
	//in-tick read (target particles tick before their followers there),
	//invisible under the followers' eases (the design's prev-frame-lane
	//strategy):
	public readonly followIdx: Int32Array;
	public readonly followRadius: Float32Array; //_folllowOffsetRadius
	public readonly followAngleDeg: Float32Array;

	//Ease-lerp lanes (stage 6) — Inertia.lerpEase (setEase(config, true,
	//frames)): the ease VALUE itself steps by `step` once per TICK for
	//`frames` ticks — not deltaTime-scaled, reference behaviour. Only the x/y
	//Inertias ever ease-lerp on the home flow (SpacesDepartment squarePaths'
	//1s counter; the reference lerp path passes easeConfig.rotation into the
	//alpha lerpEase — a bug that keeps alpha ease-lerp unreachable from
	//{x, y} configs):
	public readonly easeLerpStepX: Float32Array;
	public readonly easeLerpFramesX: Float32Array;
	public readonly easeLerpStepY: Float32Array;
	public readonly easeLerpFramesY: Float32Array;

	//Trail lanes (stage 6) — ParticleTrail (the systems dotted-line rail
	//dots): alpha fades by trailStep per frame-normalised tick; scale is
	//written CONSTANT every tick (the reference decays _scaleStep, not
	//_scale — the dots never shrink; kept). trailOn gates the
	//SystemsDepartment's trail pass; on fade-out the lane frees back to the
	//department's pool free-list but stays FLAG_ACTIVE at alpha 0, exactly
	//like the reference (ParticleTrail deactivates itself, not its particle):
	public readonly trailOn: Uint8Array;
	public readonly trailAlpha: Float32Array;
	public readonly trailStep: Float32Array;
	public readonly trailScale: Float32Array;

	private _pools: Record<PoolName, PoolRange>;

	constructor() {
		this.capacity = HomeFieldConfig.total();

		this.x = new Float32Array(this.capacity);
		this.y = new Float32Array(this.capacity);
		this.scale = new Float32Array(this.capacity);
		this.alpha = new Float32Array(this.capacity);
		this.tint = new Uint32Array(this.capacity);
		this.textureId = new Uint8Array(this.capacity);
		this.stateFlags = new Uint32Array(this.capacity);
		this.behaviourMask = new Uint32Array(this.capacity);

		this.tx = new Float32Array(this.capacity);
		this.ty = new Float32Array(this.capacity);
		this.easeX = new Float32Array(this.capacity);
		this.easeY = new Float32Array(this.capacity);
		this.tScale = new Float32Array(this.capacity);
		this.easeScale = new Float32Array(this.capacity);
		this.tAlpha = new Float32Array(this.capacity);
		this.easeAlpha = new Float32Array(this.capacity);
		//Reference creation-state eases: ParticleProperties' ctor Inertias
		//(x/y 8, scale 5, alpha 3) immediately overwritten by HomeParticle.
		//_defaultState's setEase({x: 1, y: 1, scale: 1}) — so every particle
		//STARTS at x/y/scale 1, alpha 3 until a department changes it. The
		//screens phases rely on these (e.g. the halftone never sets
		//scale/alpha eases); a zero default would divide-by-zero in the
		//integrator (found the hard way in stage 5):
		this.easeX.fill(1);
		this.easeY.fill(1);
		this.easeScale.fill(1);
		this.easeAlpha.fill(3);

		this.relX = new Float32Array(this.capacity);
		this.relY = new Float32Array(this.capacity);
		this.xOffset = new Float32Array(this.capacity);
		this.parallaxPower = new Float32Array(this.capacity);
		this.pTx = new Float32Array(this.capacity);
		this.pTy = new Float32Array(this.capacity);

		this.mpX = new Float32Array(this.capacity);
		this.mpY = new Float32Array(this.capacity);
		this.mpEaseX = new Float32Array(this.capacity);
		this.mpEaseY = new Float32Array(this.capacity);
		this.mpPowX = new Float32Array(this.capacity);
		this.mpPowY = new Float32Array(this.capacity);

		this.repellType = new Uint8Array(this.capacity);
		this.repellRadius = new Float32Array(this.capacity);
		this.repellPower = new Float32Array(this.capacity);
		this.tScaleSaved = new Float32Array(this.capacity);

		this.orbitPath = new PathSlotLanes(this.capacity, LOOKUP_CIRCLE);
		this.iconPath = new PathSlotLanes(this.capacity, LOOKUP_ICON);
		this.rotation = new Float32Array(this.capacity);

		this.breathBase = new Float32Array(this.capacity);
		this.breathChange = new Float32Array(this.capacity);
		this.breathMax = new Float32Array(this.capacity);
		this.breathAdd = new Float32Array(this.capacity);
		this.breathDir = new Float32Array(this.capacity);
		this.breathDir.fill(1);

		this.vecFlags = new Uint8Array(this.capacity);
		this.vecIncX = new Float32Array(this.capacity);
		this.vecIncY = new Float32Array(this.capacity);
		this.vecMaxX = new Float32Array(this.capacity);
		this.vecMaxY = new Float32Array(this.capacity);
		this.vecDampX = new Float32Array(this.capacity);
		this.vecDampY = new Float32Array(this.capacity);
		this.vecReduceAtX = new Float32Array(this.capacity);
		this.vecReduceAtY = new Float32Array(this.capacity);
		this.vecForceX = new Float32Array(this.capacity);
		this.vecForceY = new Float32Array(this.capacity);
		//Inertia field defaults (never observable before a setVectorConfig
		//call flips the axis on, but kept for state parity):
		this.vecIncX.fill(0.001);
		this.vecIncY.fill(0.001);
		this.vecMaxX.fill(0.2);
		this.vecMaxY.fill(0.2);
		this.vecDampX.fill(0.87);
		this.vecDampY.fill(0.87);
		this.vecReduceAtX.fill(2);
		this.vecReduceAtY.fill(2);

		this.repellFlags = new Uint8Array(this.capacity);
		this.repellReturnX = new Float32Array(this.capacity);
		this.repellReturnY = new Float32Array(this.capacity);
		this.repellReturnPowX = new Float32Array(this.capacity);
		this.repellReturnPowY = new Float32Array(this.capacity);
		//ParticleRepell.returnPower default {x: 0.08, y: 0.08}:
		this.repellReturnPowX.fill(0.08);
		this.repellReturnPowY.fill(0.08);

		this.vx = new Float32Array(this.capacity);
		this.vy = new Float32Array(this.capacity);
		this.gravity = new Float32Array(this.capacity);
		this.gravity.fill(0.0002); //ParticlePhysics._gravity default
		this.physFlags = new Uint8Array(this.capacity);
		this.physFlags.fill(PHYS_Y_BOUNCE | PHYS_BOUNDARY); //_allowYBounce / _processBoundaryTick defaults
		this.hitCooldown = new Float32Array(this.capacity);
		this.lerpStartX = new Float32Array(this.capacity);
		this.lerpStartY = new Float32Array(this.capacity);
		this.lerpEndX = new Float32Array(this.capacity);
		this.lerpEndY = new Float32Array(this.capacity);
		this.lerpEase = new Uint8Array(this.capacity);
		this.lerpFrames = new Float32Array(this.capacity);
		this.lerpTargetFrames = new Float32Array(this.capacity);
		this.physTargetSlot = new Uint8Array(this.capacity);
		this.tgtIncX = new Float32Array(this.capacity);
		this.tgtIncY = new Float32Array(this.capacity);
		this.tgtDirX = new Float32Array(this.capacity);
		this.tgtDirY = new Float32Array(this.capacity);

		this.depthVal = new Float32Array(this.capacity);
		this.depthTarget = new Float32Array(this.capacity);
		this.depthVal.fill(1); //Inertia(5, 1)
		this.depthTarget.fill(1);
		this.depthRatio = new Float32Array(this.capacity);
		this.depthBounceDir = new Float32Array(this.capacity);
		this.depthBounceSpeed = new Float32Array(this.capacity);
		this.depthFlags = new Uint8Array(this.capacity);
		this.depthFlags.fill(DEPTH_BOUNCE);
		this.depthScaleMin = new Float32Array(this.capacity);
		this.depthScaleMax = new Float32Array(this.capacity);
		this.depthAlphaMin = new Float32Array(this.capacity);
		this.depthAlphaMax = new Float32Array(this.capacity);
		//ParticleDepth defaults: _scale {0.1, 0.43}, _alpha {0.1, 0.7}:
		this.depthScaleMin.fill(0.1);
		this.depthScaleMax.fill(0.43);
		this.depthAlphaMin.fill(0.1);
		this.depthAlphaMax.fill(0.7);
		this.origX = new Float32Array(this.capacity);
		this.origY = new Float32Array(this.capacity);
		//ParticleDepth ctor randomises bounce per particle:
		//  _bounceDirection = ±1; _bounceSpeed = 0.01 + rand * 0.09:
		for (let i = 0; i < this.capacity; i++) {
			this.depthBounceDir[i] = Math.random() < 0.5 ? 1 : -1;
			this.depthBounceSpeed[i] = 0.01 + Math.random() * 0.09;
		}

		this.blendAdd = new Uint8Array(this.capacity);

		this.followIdx = new Int32Array(this.capacity);
		this.followIdx.fill(-1);
		this.followRadius = new Float32Array(this.capacity);
		this.followRadius.fill(0.03); //ParticleProgressParallax._folllowOffsetRadius default
		this.followAngleDeg = new Float32Array(this.capacity);

		this.easeLerpStepX = new Float32Array(this.capacity);
		this.easeLerpFramesX = new Float32Array(this.capacity);
		this.easeLerpStepY = new Float32Array(this.capacity);
		this.easeLerpFramesY = new Float32Array(this.capacity);

		this.trailOn = new Uint8Array(this.capacity);
		this.trailAlpha = new Float32Array(this.capacity);
		this.trailStep = new Float32Array(this.capacity);
		this.trailScale = new Float32Array(this.capacity);

		//HomeParticle.createBehaviours randomises BOTH path slots on every
		//particle at creation (orbitPath.randomise() / iconPath.randomise():
		//pathIndex = scaleIndex = rand * (table.length - 1)) — without this
		//the whole pool shares one orbit phase and the icon cloud collapses
		//into a coherent smear instead of the reference's isotropic fuzz:
		const lookups = getPathLookups();
		const circleMax = lookups[LOOKUP_CIRCLE].length - 1;
		const iconMax = lookups[LOOKUP_ICON].length - 1;
		for (let i = 0; i < this.capacity; i++) {
			this.orbitPath.idx[i] = this.orbitPath.scaleIdx[i] = Math.random() * circleMax;
			this.iconPath.idx[i] = this.iconPath.scaleIdx[i] = Math.random() * iconMax;
		}

		//Construction-state rel positions (stage-8 catch): the reference field
		//builds EVERY home particle on layouts.grid over the home field rect —
		//ParticleField._createConfig -> GridLayout.get(HomeFieldConfig.total(),
		//{x: 0.33333333, y: 0}, {w: 0.33333, h: 1.0}), cols 40 — and arms that
		//read position before writing it (spaces Rain's fall-in, the pixel-
		//image lerp starts) inherit that spread. Unseeded lanes put the whole
		//spaces pool at rel (0,0), so the first Rain pass fell in a single
		//centre column instead of the reference's field-wide fall-in. px lanes
		//stay 0 like before: every pool is hidden until its first arm, and
		//the first kernel pass recomputes px from rel:
		{
			const cols = 40;
			const rows = Math.round(HomeFieldConfig.total() / cols);
			for (let i = 0; i < this.capacity; i++) {
				this.relX[i] = 0.33333333 + (i % cols) * (0.33333 / cols);
				this.relY[i] = 0 + Math.floor(i / cols) * (1.0 / rows);
			}
		}

		//Pool ranges from the shared partition table (upper is inclusive there;
		//empty pools — transitions ships 0 — come out as count 0, end == start):
		const indexes = HomeFieldConfig.indexes();
		this._pools = {} as Record<PoolName, PoolRange>;
		POOL_NAMES.forEach((name) => {
			const slice = indexes[name];
			const start = Math.max(0, slice.lower);
			const end = Math.max(start, slice.upper + 1);
			this._pools[name] = { name: name, start: start, end: end, count: end - start };
		});
	}

	public pool(name: PoolName): PoolRange {
		return this._pools[name];
	}

	public pools(): ReadonlyArray<PoolRange> {
		return POOL_NAMES.map((name) => this._pools[name]);
	}

	public activate(range: PoolRange): void {
		for (let i = range.start; i < range.end; i++) {
			this.stateFlags[i] |= FLAG_ACTIVE;
		}
	}

	public deactivate(range: PoolRange): void {
		for (let i = range.start; i < range.end; i++) {
			this.stateFlags[i] &= ~FLAG_ACTIVE;
		}
	}
}

/**
 * Inertia.setVectorConfig, ported lane-for-field (stage 5). The reference
 * ALWAYS rewrites the rates — even when switching the mode OFF — with a
 * device-width damping and a fresh per-call randomisation; kept verbatim:
 *
 *   r = 1 - min(1, innerWidth / 1600)
 *   increaseRate' = (rate - rate*0.6*r) * (0.5 + rand*0.5)
 *   maxForce'     = (max  - max*0.5*r)  * (0.5 + rand*0.5)
 *   reduceForceAt = 1 - r*0.3
 *
 * The running _vectorForce is NOT reset by the reference — the lanes keep it.
 * `axis` is VEC_X or VEC_Y (setVectorStyleMovement calls once per axis).
 */
export function writeVectorConfig(
	core: ParticleCore,
	lane: number,
	axis: number,
	active: boolean,
	increaseRate: number = 0.3,
	maxForce: number = 3,
	dampen: number = 0.87
): void {
	const r = 1 - Math.min(1, device.innerWidth / 1600);
	const inc = (increaseRate - increaseRate * 0.6 * r) * (0.5 + Math.random() * 0.5);
	const max = (maxForce - maxForce * 0.5 * r) * (0.5 + Math.random() * 0.5);
	const reduceAt = 1 - r * 0.3;

	if (active) {
		core.vecFlags[lane] |= axis;
	} else {
		core.vecFlags[lane] &= ~axis;
	}

	if (axis === VEC_X) {
		core.vecIncX[lane] = inc;
		core.vecMaxX[lane] = max;
		core.vecDampX[lane] = dampen;
		core.vecReduceAtX[lane] = reduceAt;
	} else {
		core.vecIncY[lane] = inc;
		core.vecMaxY[lane] = max;
		core.vecDampY[lane] = dampen;
		core.vecReduceAtY[lane] = reduceAt;
	}
}
